import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { useImpression } from '@nerdwallet/analytics/react/impression';
import BaseOnboardingModal, {
  StepsArrayType,
  StepsInterface,
} from '@nerdwallet/calculator-onboarding-modal';
import { enableCSAT } from '@nerdwallet/nw-client-lib/csat';

import {
  postOnboardingCalculate,
  saveHomeCalculatorProfile,
} from '../../lib/actions';
import saveToProfile from '../../lib/actions/save-to-profile';
import {
  EVENT_TYPE,
  FORM_NAME,
  ENTITY_NAME,
  SECTION_NAME,
} from '../../lib/analytics-constants';
import { validationsSelector } from '../../lib/selectors';
import { manualInputsSelector } from '../../lib/selectors/home-affordability-calculator-selectors';
import {
  hasNoErrors,
  ReducerInputsData,
  Validations,
  HACalculatorField,
} from '../../lib/utils';

import {
  PurchaseLocationInput,
  HouseholdIncomeInput,
  DownPaymentInput,
  MonthlyDebtInput,
  CreditScoreInput,
} from './inputs';

interface HomeAffordabilityOnboardingProps
  extends Pick<
    ReducerInputsData<HACalculatorField>,
    'manualInputs' | 'validations'
  > {
  location: Record<string, any>;
  browser: Record<string, any>;
  onClose: () => void;
  isOpen: boolean;
  postOnboardingCalculateAction: (isOnboardingCompleted: boolean) => void;
  saveHomeCalculatorProfileAction: (p: Record<string, any>) => void;
  saveToProfileAction: () => void;
}

const getPurchaseLocationStep = (): StepsInterface => {
  // Currently we don't disable next on error

  const analytics = {
    enabled: true,
    payload: {
      bedrock: {
        eventType: EVENT_TYPE.US_LOCATION_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.HOME_AFFORDABILITY,
        page_number: 1,
        total_pages: 5,
      },
    },
  };

  return {
    title: 'Where are you looking to buy?',
    titleHelp: 'The amount of your loan when you first took it out.',
    content: (
      <PurchaseLocationInput
        analytics={analytics}
        label="Type city or ZIP code"
      />
    ),
    secondaryContent:
      'Location matters. Affordability varies based on your state (interest rates) and even your county (property taxes).',
  };
};

const getHouseholdStep = (validations: Validations): StepsInterface => {
  const errorMessage = validations?.income?.[0];
  const error = Boolean(errorMessage);

  const analytics = {
    enabled: true,
    payload: {
      bedrock: {
        eventType: EVENT_TYPE.INCOME_PRETAX_INCOME_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.HOME_AFFORDABILITY,
        page_number: 2,
        total_pages: 5,
      },
    },
  };

  return {
    title: 'What is your annual household income before taxes?',
    titleHelp: 'Combine your income if buying with a partner',
    content: <HouseholdIncomeInput analytics={analytics} />,
    isNextDisabled: error,
    secondaryContent:
      'A home is a big expense – but it also pays in other ways! Deduct your mortgage interest and local property taxes to save money at tax time (especially when interest is high in the early years).',
  };
};

const getDownPaymentStep = (validations: Validations): StepsInterface => {
  const errorMessage = validations?.downPayment?.[0];
  const error = Boolean(errorMessage);

  const analytics = {
    enabled: true,
    payload: {
      bedrock: {
        eventType: EVENT_TYPE.HOME_DOWN_PAYMENT_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.HOME_AFFORDABILITY,
        page_number: 3,
        total_pages: 5,
      },
    },
  };

  return {
    title: 'How much is your down payment?',
    content: <DownPaymentInput analytics={analytics} />,
    isNextDisabled: error,
    secondaryContent:
      'Lenders have traditionally preferred a 20% down payment, but these days you can put much less down. However, that means you’ll likely need to pay mortgage insurance.',
  };
};

const getMonthlyDebtStep = (validations: Validations): StepsInterface => {
  const errorMessage = validations?.monthlyDebt?.[0];
  const error = Boolean(errorMessage);

  const analytics = {
    enabled: true,
    payload: {
      bedrock: {
        eventType: EVENT_TYPE.MONTHLY_OBLIGATIONS_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.HOME_AFFORDABILITY,
        page_number: 4,
        total_pages: 5,
      },
    },
  };

  return {
    title:
      'How much are your monthly obligations, besides housing and everyday necessities like food?',
    titleHelp:
      'Include your minimum payments for credit cards, student loans, car payments (exclude housing and living expenses).',
    content: <MonthlyDebtInput analytics={analytics} />,
    isNextDisabled: error,
    secondaryContent: `Monthly obligations could include things like child care or support, car loans, credit card debt, student loans and alimony.
    Lenders have some leeway on how they factor in your debt load — depending on your credit score and payment history —
    but it’s best to consider realistic numbers here, not low-ball numbers so that you don’t get into a financial bind down the line.`,
  };
};

const getCreditScoreStep = (closeOnboardingModal): StepsInterface => {
  const analytics = {
    enabled: true,
    payload: {
      bedrock: {
        eventType: EVENT_TYPE.CREDIT_SCORE_BAND_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.HOME_AFFORDABILITY,
        page_number: 5,
        total_pages: 5,
      },
    },
  };

  return {
    title: 'What is your credit score?',
    content: (
      <CreditScoreInput
        analytics={analytics}
        closeOnboardingModal={closeOnboardingModal}
      />
    ),
    secondaryContent:
      'A higher credit score gives you a better chance for a lower loan interest rate – which could save you thousands of dollars over time.',
  };
};

const generateSteps = (
  validations: Validations,
  closeOnboardingModal
): StepsArrayType => {
  const steps: StepsArrayType = [
    getPurchaseLocationStep(),
    getHouseholdStep(validations),
    getDownPaymentStep(validations),
    getMonthlyDebtStep(validations),
    getCreditScoreStep(closeOnboardingModal),
  ];

  return steps;
};

const HomeAffordabilityOnboardingModal: React.FC<
  HomeAffordabilityOnboardingProps
> = (props) => {
  const {
    browser,
    isOpen,
    location,
    manualInputs,
    onClose: onCloseProp,
    postOnboardingCalculateAction,
    saveHomeCalculatorProfileAction,
    saveToProfileAction,
    validations,
  } = props;

  const [step, updateStep] = useState(0);

  const errorsString = JSON.stringify(validations);
  const inputsValues = JSON.stringify(manualInputs);

  const onClose = useCallback(
    (wasSubmitted: boolean) => {
      // prevent closing if there's any error
      if (!hasNoErrors(validations)) return;

      postOnboardingCalculateAction(wasSubmitted);
      // Even if the user has exited out we still count it as having completed onboarding

      saveHomeCalculatorProfileAction({
        hasCompletedOnboardingHac: true,
        ...manualInputs,
      });

      saveToProfileAction();

      if (!browser.isMobile) {
        enableCSAT();
      }
      onCloseProp();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      errorsString,
      location.pathname,
      location.search,
      location.hash,
      inputsValues,
    ]
  );

  const onStepChanged = useCallback((newStep: number) => {
    // we need to update the step value so we trigger a new impression
    updateStep(newStep);
  }, []);

  const steps = generateSteps(validations, onClose);
  const modalTitle = 'How much house can I afford?';

  const { ref: impressionRef } = useImpression({
    eventType: EVENT_TYPE.FORM_IMPRESSION,
    entityName: ENTITY_NAME.MRT_HOME_AFFORDABILITY,
    payload: {
      section_name: SECTION_NAME.MRT_HOME_AFFORDABILITY,
      form_name: FORM_NAME.HOME_AFFORDABILITY,
      page_number: step + 1,
      total_pages: steps.length,
    },
  });

  return (
    <div>
      <BaseOnboardingModal
        onClose={onClose}
        onStepChanged={onStepChanged}
        isOpen={isOpen}
        modalTitle={modalTitle}
        steps={steps}
        ref={impressionRef}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  manualInputs: manualInputsSelector(state),
  validations: validationsSelector(state),
});

const mapDispatchToProps = {
  postOnboardingCalculateAction: postOnboardingCalculate,
  saveHomeCalculatorProfileAction: saveHomeCalculatorProfile,
  saveToProfileAction: saveToProfile,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(HomeAffordabilityOnboardingModal);
